import { useEffect, useRef, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { invalidatePricingCache } from '@/features/order/orderSlice';
import Layout from './components/layout/Layout';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import StudioPage from './pages/StudioPage';
import OfertaPage from './pages/OfertaPage';
import ContactPage from './pages/ContactPage';
import DiscountsPage from './pages/DiscountsPage';
import PricingPage from './pages/PricingPage';
import WalletPage from './pages/WalletPage';
import IntegrationsPage from './pages/IntegrationsPage';
import AIArtistIntegrationPage from './pages/AIArtistIntegrationPage';
import EtsyIntegrationPage from './pages/EtsyIntegrationPage';
import EtsyOrdersPage from './pages/EtsyOrdersPage';
import EtsyMatchingPage from './pages/EtsyMatchingPage';
import AdminKnowledgeBasePage from './pages/AdminKnowledgeBasePage';
import AdminOrderItemsManagementPage from './pages/AdminOrderItemsManagementPage';
import AdminUserActivityPage from './pages/AdminUserActivityPage';
import AdminTranslationsPage from './pages/AdminTranslationsPage';
import AdminExchangeRatesPage from './pages/AdminExchangeRatesPage';
import AdminNovapostCachePage from './pages/AdminNovapostCachePage';
import AdminCreatorApplicationsPage from './pages/AdminCreatorApplicationsPage';
import AdminPackagingMaterialsPage from './pages/AdminPackagingMaterialsPage';
import AdminTelegramPage from './pages/AdminTelegramPage';
import AdminOrderRefundPage from './pages/AdminOrderRefundPage';
import AdminHomepageSliderPage from './pages/AdminHomepageSliderPage';
import AdminPromotionsPage from './pages/AdminPromotionsPage';
import AdminPaperTypesPage from './pages/AdminPaperTypesPage';
import AdminPaymentsPage from './pages/AdminPaymentsPage';
import EmailConfirmationPage from './pages/EmailConfirmationPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

import { useAppDispatch, useAppSelector } from './app/hooks';
import {
  selectIsAuthenticated,
  selectTemporaryUserId,
  setAuthCredentials,
  setTemporaryUserId,
} from './features/auth/authSlice';
import { setCurrentService, selectCurrentService } from './features/service/serviceSlice';
import { getActiveServices } from './services/serviceService';
import { useAnalytics } from './hooks/useAnalytics';
import AppServices from './components/AppServices';
import i18n, { configureSupportedLanguages, STATIC_SUPPORTED_LANGUAGES } from './i18n';
import { useToast } from './hooks/use-toast';
import { discountApi } from './services/discountApi';
import { getRefundNotifications, acknowledgeRefundNotification } from './services/userService';
import { refresh as refreshAuthSession } from './services/authService';
import { logging } from './services/logging';
import {
  clearAuthAttemptPending,
  markAuthSessionLostFromPendingAttempt,
} from './utils/authAttemptStorage';

function App() {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const temporaryUserId = useAppSelector(selectTemporaryUserId);
  const currentService = useAppSelector(selectCurrentService);
  const lastInvalidatedDiscountIdRef = useRef<string | null>(null);
  const analytics = useAnalytics();
  const { toast } = useToast();
  const location = useLocation();
  const [isI18nInitialized, setIsI18nInitialized] = useState(false);

  useEffect(() => {
    configureSupportedLanguages(STATIC_SUPPORTED_LANGUAGES)
      .then(() => {
        setIsI18nInitialized(true);
      })
      .catch(() => {
        // Still allow app to render if i18n init fails.
        setIsI18nInitialized(true);
      });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const hydrateSession = async () => {
      if (isAuthenticated) {
        return;
      }

      try {
        const user = await refreshAuthSession();
        if (!cancelled) {
          dispatch(setAuthCredentials(user));
          clearAuthAttemptPending();
          logging.info('auth.session_hydrate_succeeded', {
            source: 'app_startup',
          });
        }
      } catch (error) {
        // No active session cookie, keep anonymous state.
        if (!cancelled) {
          const hadPendingAuthAttempt = markAuthSessionLostFromPendingAttempt();
          logging.warn('auth.session_hydrate_failed', {
            source: 'app_startup',
            hadPendingAuthAttempt,
            error,
          });
        }
      }
    };

    void hydrateSession();

    return () => {
      cancelled = true;
    };
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    // On startup, ensure there is a temporary user ID for anonymous users.
    // The actual session check for logged-in users is handled by API calls
    // when navigating to protected routes.
    if (!isAuthenticated && !temporaryUserId) {
      const newTemporaryId = uuidv4();
      dispatch(setTemporaryUserId(newTemporaryId));
    }
  }, [dispatch, isAuthenticated, temporaryUserId]);

  // Set default serviceId to 'print_art_wizard' if not set yet
  useEffect(() => {
    let cancelled = false;
    const ensureDefaultService = async () => {
      if (currentService) return; // already set
      try {
        const services = await getActiveServices();
        if (cancelled) return;
        const defaultSvc = services.find(s => s.name === 'print_art_wizard');
        if (defaultSvc?.id) {
          dispatch(setCurrentService(defaultSvc));
        }
      } catch {
        // ignore
      }
    };
    ensureDefaultService();
    return () => { cancelled = true; };
  }, [dispatch, currentService]);

  useEffect(() => {
    if (isI18nInitialized) {
      analytics.page(location.pathname, { title: document.title });
    }
  }, [analytics, location.pathname, isI18nInitialized]);

  // Check for discount notifications (on login and every 10 minutes)
  useEffect(() => {
    let cancelled = false;
    const checkDiscountNotification = async () => {
      if (!isAuthenticated) return;
      try {
        const discount = await discountApi.getMyDiscount();
        if (cancelled || !discount?.shouldNotify) return;

        const discountCacheKey = discount.id ?? `${discount.type ?? 'discount'}:${discount.appliedAt ?? 'unknown'}`;
        if (lastInvalidatedDiscountIdRef.current !== discountCacheKey) {
          dispatch(invalidatePricingCache());
          lastInvalidatedDiscountIdRef.current = discountCacheKey;
        }

        let acknowledged = false;
        toast({
          title: i18n.t('discountPrograms.notification.toastTitle', 'Your {{percent}}% discount is active', {
            percent: discount.percent,
          }),
          description: undefined,
          variant: 'default',
          duration: Number.POSITIVE_INFINITY,
          onOpenChange: (open) => {
            if (open || cancelled || acknowledged) {
              return;
            }
            acknowledged = true;
            void discountApi.acknowledgeDiscountNotification().catch(() => {
              acknowledged = false;
            });
          },
        });
      } catch {
        // ignore notification failures
      }
    };

    // Check immediately on mount/login
    checkDiscountNotification();

    // Check every 10 minutes (600000ms) for long-running sessions
    const interval = setInterval(checkDiscountNotification, 10 * 60 * 1000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [dispatch, isAuthenticated, toast]);

  // Check for refund notifications (on login and every 5 minutes)
  useEffect(() => {
    let cancelled = false;
    const checkRefundNotifications = async () => {
      if (!isAuthenticated) return;
      try {
        const notifications = await getRefundNotifications();
        if (cancelled || notifications.length === 0) return;

        // Show toast for each pending refund notification
        notifications.forEach((notification) => {
          let acknowledged = false;
          toast({
            title: i18n.t('refund.notification.toastTitle', 'Refund Processed'),
            description: i18n.t(
              'refund.notification.toastDescription',
              'You have received a refund of {{amount}} {{currency}} for order {{orderId}}.',
              {
                amount: notification.amount.toFixed(2),
                currency: notification.currency,
                orderId: notification.orderId ?? 'N/A',
              }
            ),
            variant: 'default',
            duration: Number.POSITIVE_INFINITY,
            onOpenChange: (open) => {
              if (open || cancelled || acknowledged) {
                return;
              }
              acknowledged = true;
              void acknowledgeRefundNotification(notification.id).catch(() => {
                acknowledged = false;
              });
            },
          });
        });
      } catch {
        // ignore notification failures
      }
    };

    // Check immediately on mount/login
    checkRefundNotifications();

    // Check every 10 minutes (600000ms) for long-running sessions
    const interval = setInterval(checkRefundNotifications, 10 * 60 * 1000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [dispatch, isAuthenticated, toast]);

  // Detect referral code in URL and store it
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      localStorage.setItem('tulasi.referralCode', refCode);
    }
  }, [location.search]);

  if (!isI18nInitialized) {
    // Render a minimal loading state that does not involve the main Layout
    return <div>Loading...</div>; // Or a more sophisticated loading spinner
  }

  return (
    <Layout>
      <AppServices />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/studio" element={<StudioPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/oferta" element={<OfertaPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/discounts" element={<DiscountsPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/wallet" element={<WalletPage />} />
        <Route path="/integrations" element={<IntegrationsPage />} />
        <Route path="/integrations/etsy" element={<EtsyIntegrationPage />} />
        <Route path="/integrations/ai-artist" element={<AIArtistIntegrationPage />} />
        <Route path="/integrations/etsy/orders" element={<EtsyOrdersPage />} />
        <Route path="/integrations/etsy/matching" element={<EtsyMatchingPage />} />
        <Route path="/admin/knowledge-base" element={<AdminKnowledgeBasePage />} />
        <Route path="/admin/orders" element={<AdminOrderItemsManagementPage initialMode="orders" />} />
        <Route path="/admin/print-orders" element={<AdminOrderItemsManagementPage initialMode="items" />} />
        <Route path="/admin/user-activity" element={<AdminUserActivityPage />} />
        <Route path="/admin/translations" element={<AdminTranslationsPage />} />
        <Route path="/admin/exchange-rates" element={<AdminExchangeRatesPage />} />
        <Route path="/admin/novapost-cache" element={<AdminNovapostCachePage />} />
        <Route path="/admin/telegram" element={<AdminTelegramPage />} />
        <Route path="/admin/creator-applications" element={<AdminCreatorApplicationsPage />} />
        <Route path="/admin/packaging-materials" element={<AdminPackagingMaterialsPage />} />
        <Route path="/admin/homepage-slider" element={<AdminHomepageSliderPage />} />
        <Route path="/admin/promotions" element={<AdminPromotionsPage />} />
        <Route path="/admin/paper-types" element={<AdminPaperTypesPage />} />
        <Route path="/admin/payments" element={<AdminPaymentsPage />} />
        <Route path="/admin/order-refund" element={<AdminOrderRefundPage />} />
        <Route path="/confirm-email" element={<EmailConfirmationPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Routes>
    </Layout>
  );
}

export default App;
